// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyHome } from "@/types";
import { formatNumber, formatStorage } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import OrganizationCard from "@/UI/cards/OrganizationCard";
import StatCard from "@/UI/cards/StatCard";
import SubscriptionCard from "@/UI/cards/SubscriptionCard";
import AttentionPanel, { type TyAttentionItem } from "@/UI/components/AttentionPanel";
import ListPanel from "@/UI/components/ListPanel";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The admins' first screen, and **a worklist before it is a report.**
 *
 * Billing here is manual, which is the whole reason this screen is shaped the way it is: a transfer
 * nobody confirms is a company that paid and has no access, and a period nobody renews is a company
 * that loses access on a date. Both are jobs with a deadline, so they are at the top, and the totals
 * underneath are only context. An admin who opens this app and has nothing to do should be able to
 * see that in one second — which is why the attention panel says so in one line instead of drawing
 * four reassuring zeroes.
 *
 * Everything comes from one request. The two lists under it are previews that hand off to the
 * sections that own them: the cards open, and the actions live where the whole list is.
 */
export default function HomeScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const admin = useAuth((state) => state.admin);

    const home = useFetch<TyHome>("/home");

    const hour = new Date().getHours();
    const greeting = hour < 12 ? t.home.greetingMorning : hour < 17 ? t.home.greetingAfternoon : t.home.greetingEvening;

    const data = home.data;

    const attention: TyAttentionItem[] = [
        { key: "payments", label: t.home.attentionPayments, count: data?.attention.payments ?? 0, to: "/subscriptions?tab=pending", tone: "bad" },
        { key: "grace", label: t.home.attentionGrace, count: data?.attention.grace ?? 0, to: "/subscriptions?tab=live", tone: "bad" },
        {
            key: "storageRequests",
            label: t.home.attentionStorageRequests,
            count: data?.attention.storageRequests ?? 0,
            to: "/subscriptions?tab=storage",
            tone: "warn",
        },
        /** A request nobody has picked up. The same kind of debt as an unconfirmed transfer. */
        { key: "tickets", label: t.home.attentionTickets, count: data?.attention.tickets ?? 0, to: "/support", tone: "bad" },
        { key: "ending", label: t.home.attentionEnding, count: data?.attention.ending ?? 0, to: "/subscriptions?tab=ending", tone: "warn" },
        { key: "storage", label: t.home.attentionStorage, count: data?.attention.storage ?? 0, to: "/organizations", tone: "warn" },
    ];

    return (
        <section className="home-screen">
            <ListPanel title={admin?.name ? `${greeting} · ${admin.name}` : greeting} subtitle={t.home.subtitle} />

            {home.loading && <p className="screen-state">{t.common.loading}</p>}
            {home.error && <p className="screen-state screen-state--error">{home.error}</p>}

            {data && (
                <>
                    <AttentionPanel items={attention} />

                    <div className="stat-row">
                        <StatCard icon="building" value={formatNumber(data.totals.organizations)} label={t.home.organizations} />
                        <StatCard icon="subscription" value={formatNumber(data.totals.activeSubscriptions)} label={t.home.activeSubscriptions} />
                        <StatCard icon="team" value={formatNumber(data.totals.clients)} label={t.home.clients} />
                        <StatCard icon="drive" value={formatStorage(data.totals.storageUsedMb, lang)} label={t.home.storageUsed} />
                    </div>

                    {/* --- What is running out, which is the one list with a date on it --------- */}
                    {data.endingSoon.length > 0 && (
                        <section className="home-screen__section">
                            <header className="home-screen__section-head">
                                <h2 className="home-screen__section-title">{t.home.endingSoon}</h2>
                                <button type="button" className="home-screen__see-all" onClick={() => navigate("/subscriptions?tab=ending")}>
                                    <span>{t.home.seeAll}</span>
                                    <Icon name="chevron" size={14} />
                                </button>
                            </header>

                            <div className="list-screen__rows">
                                {data.endingSoon.map((row) => (
                                    <SubscriptionCard
                                        key={row._id}
                                        subscription={row}
                                        onOpen={() => navigate(`/organizations/${row.organization.slug}?tab=subscription`)}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* --- The newest companies: the same card their own list draws ------------- */}
                    {data.newest.length > 0 && (
                        <section className="home-screen__section">
                            <header className="home-screen__section-head">
                                <h2 className="home-screen__section-title">{t.home.newest}</h2>
                                <button type="button" className="home-screen__see-all" onClick={() => navigate("/organizations")}>
                                    <span>{t.home.seeAll}</span>
                                    <Icon name="chevron" size={14} />
                                </button>
                            </header>

                            <div className="list-screen__grid">
                                {data.newest.map((organization) => (
                                    <OrganizationCard
                                        key={organization._id}
                                        organization={organization}
                                        onOpen={() => navigate(`/organizations/${organization.slug}`)}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}
        </section>
    );
}
